import { css } from '@emotion/css';

import { mobileMediaQuery, nonMobileMediaQuery } from '../../constants';
import { m } from '../../motif/m';
import { directionalOverlayPanelActiveCss } from '../DirectionalOverlay/DirectionalOverlay.styles';
import { desktopSideNavWidthPx } from '../SideNavigation';

// Value taken from designs. Jira ticket: https://jira.sc-corp.net/browse/WEBP-10648
const contentMaxWidthPx = 340;
const contentMaxWidthPxMobile = 640;

/**
 * Shared styles for all SideOverlay directions: background, border, dimensions, and mobile sheet.
 * Combines with a direction-specific class (`sideOverlayDirectionRightCss` or
 * `sideOverlayDirectionLeftCss`) which overrides the `right`/`left` position set by
 * `DirectionalOverlay`'s base direction class.
 */
export const sideOverlayCss = css`
  /* Extra clearance beyond 100% so the panel clears the page container edge. */
  --directional-overlay-lateral-outset: calc(2 * ${m('--spacing-l')});

  background-color: ${m('--side-overlay-bg-color')};
  border: ${m('--border-width-xs')} solid ${m('--side-overlay-border-color')};
  box-shadow: 0 12px 20px 0 #0000001f;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  ${nonMobileMediaQuery} {
    border-radius: ${m('--border-radius-l')};
    height: calc(100vh - var(--total-header-height, 0px) - (2 * ${m('--spacing-l')}));
    max-width: ${contentMaxWidthPx}px;
    top: calc(var(--total-header-height, 0px) + ${m('--spacing-l')});
    width: ${contentMaxWidthPx}px;
  }

  ${mobileMediaQuery} {
    border-radius: ${m('--border-radius-l')} ${m('--border-radius-l')} 0 0;
    bottom: 0;
    height: auto;
    left: 0;
    margin-left: auto;
    margin-right: auto;
    max-height: calc(100vh - var(--total-header-height, 0px) - ${m('--spacing-m')});
    max-width: ${contentMaxWidthPxMobile}px;
    right: 0;
    width: calc(100vw - (2 * ${m('--spacing-m')}));

    *[dir='rtl'] & {
      left: 0;
      right: 0;
    }
  }
`;

/**
 * Right-docked position, overriding the `right: 0` from `directionalOverlayDesktopRightCss`.
 * Uses `&&` to guarantee higher specificity regardless of stylesheet insertion order.
 */
export const sideOverlayDirectionRightCss = css`
  ${nonMobileMediaQuery} {
    /*
     * 50%              --> move to center of the viewport (from the right)
     * 50cqw            --> move back by half of the page container width to reach its right edge
     * var(--spacing-l) --> adjust to move away from the container edge
     */
    && {
      right: calc(50% - 50cqw + ${m('--spacing-l')});
    }

    *[dir='rtl'] & {
      left: calc(50% - 50cqw + ${m('--spacing-l')});
      right: auto;
    }

    /* RTL: negate the direction-class transform so the panel slides from the left. */
    *[dir='rtl'] &:not(.${directionalOverlayPanelActiveCss}) {
      transform: translateX(calc(-100% - var(--directional-overlay-lateral-outset, 0px)));
    }
  }
`;

/** Left-docked position, overriding the `left: 0` from `directionalOverlayDesktopLeftCss`. */
export const sideOverlayDirectionLeftCss = css`
  ${nonMobileMediaQuery} {
    && {
      left: calc(50% - 50cqw + ${m('--spacing-l')});
      right: auto;
    }

    *[dir='rtl'] & {
      left: auto;
      right: calc(50% - 50cqw + ${m('--spacing-l')});
    }

    /* RTL: negate the direction-class transform so the panel slides from the right. */
    *[dir='rtl'] &:not(.${directionalOverlayPanelActiveCss}) {
      transform: translateX(calc(100% + var(--directional-overlay-lateral-outset, 0px)));
    }
  }
`;

export const sideOverlayWithSideNavCss = css`
  ${nonMobileMediaQuery} {
    /*
     * 50%                       --> move to center of the viewport (from the right)
     * 50cqw                     --> move back by half of the page container width to reach its right edge
     * desktopSideNavWidthPx / 2 --> move back by half of the side navigation width
     * var(--spacing-l)          --> adjust to move away from the container edge
     */
    && {
      right: calc(50% - 50cqw - ${desktopSideNavWidthPx / 2}px + ${m('--spacing-l')});
    }

    *[dir='rtl'] & {
      left: calc(50% - 50cqw - ${desktopSideNavWidthPx / 2}px + ${m('--spacing-l')});
      right: auto;
    }
  }
`;

/** Left-docked position adjusted for the side navigation width offset. */
export const sideOverlayWithSideNavDirectionLeftCss = css`
  ${nonMobileMediaQuery} {
    && {
      left: calc(50% - 50cqw - ${desktopSideNavWidthPx / 2}px + ${m('--spacing-l')});
      right: auto;
    }

    *[dir='rtl'] & {
      left: auto;
      right: calc(50% - 50cqw - ${desktopSideNavWidthPx / 2}px + ${m('--spacing-l')});
    }
  }
`;

export const sideOverlayHeaderCss = css`
  display: flex;
  font-size: ${m('--h6-desktop-font-size')};
  font-weight: ${m('--h6-desktop-font-weight')};
  justify-content: end;
  line-height: ${m('--h6-desktop-font-line-height')};
  padding: ${m('--spacing-m')};
`;

export const sideOverlayCloseButtonCss = css`
  background: transparent;
  border: none;
  cursor: pointer;
  margin: calc(${m('--spacing-m')} * -1);
  padding: ${m('--spacing-m')};
`;

export const sideOverlayListCss = css`
  height: 100%;
  margin: 0;
  overflow-y: auto;
  padding: ${m('--spacing-xs')} ${m('--spacing-l')} ${m('--spacing-l')} ${m('--spacing-l')};

  ${mobileMediaQuery} {
    padding-bottom: ${m('--spacing-xxxxl')};
  }
`;

export const sideOverlayTermCss = css`
  color: ${m('--side-overlay-fg-color')};
  font-size: ${m('--h6-desktop-font-size')};
  font-weight: ${m('--h6-desktop-font-weight')};
  line-height: ${m('--h6-desktop-font-line-height')};
  margin: 0 0 ${m('--spacing-s')} 0;
  padding: 0;
`;

export const sideOverlayDefinitionCss = css`
  color: ${m('--side-overlay-fg-color')};
  font-size: ${m('--p2-desktop-font-size')};
  font-weight: ${m('--p2-desktop-font-weight')};
  line-height: ${m('--p2-desktop-font-line-height')};
  margin: 0;
  padding: 0;
`;

/** Hides the backdrop on desktop so it only appears on mobile (sheet behavior). */
export const sideOverlayBackdropMobileOnlyCss = css`
  ${nonMobileMediaQuery} {
    display: none;
  }
`;
